import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router';
import { message } from 'antd';
import { ArrowLeft, Copy, Check, AlertCircle, CheckCircle, MinusCircle } from 'lucide-react';
import { colors, spacing, radius } from '../../../styles/theme';
import brandDnaActions from '../../../actions/brandDna';

// ── Styles ──────────────────────────────────────────────────────────────────

const PAGE = {
  padding: spacing.xl,
  maxWidth: 900,
  margin: '0 auto',
  fontFamily: 'monospace',
};

const SECTION = {
  marginBottom: spacing.lg,
  background: colors.background.secondary,
  border: `1px solid ${colors.border.secondary}`,
  borderRadius: radius.md,
  overflow: 'hidden',
};

const SECTION_HEADER = {
  padding: `${spacing.sm}px ${spacing.md}px`,
  borderBottom: `1px solid ${colors.border.secondary}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: 'rgba(255,255,255,0.02)',
};

const SECTION_TITLE = {
  fontSize: 13,
  fontWeight: 600,
  color: colors.text.primary,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

const FIELD_ROW = {
  display: 'flex',
  borderBottom: `1px solid ${colors.border.secondary}`,
  fontSize: 13,
};

const FIELD_LABEL = {
  width: 200,
  flexShrink: 0,
  padding: `${spacing.xs}px ${spacing.md}px`,
  color: colors.text.tertiary,
  borderRight: `1px solid ${colors.border.secondary}`,
  background: 'rgba(255,255,255,0.01)',
};

const FIELD_VALUE = {
  flex: 1,
  padding: `${spacing.xs}px ${spacing.md}px`,
  color: colors.text.primary,
  wordBreak: 'break-word',
  whiteSpace: 'pre-wrap',
};

const EMPTY_VALUE = {
  ...FIELD_VALUE,
  color: colors.text.disabled,
  fontStyle: 'italic',
};

const STATUS_BADGE = (filled) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 8px',
  borderRadius: 12,
  fontSize: 11,
  fontWeight: 500,
  background: filled ? 'rgba(82,196,26,0.1)' : 'rgba(255,255,255,0.05)',
  color: filled ? '#52c41a' : colors.text.disabled,
  fontFamily: 'inherit',
});

const IMAGE_THUMB = {
  width: 60,
  height: 60,
  borderRadius: radius.sm,
  objectFit: 'cover',
  border: `1px solid ${colors.border.secondary}`,
  background: colors.surface.primary,
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function renderValue(val) {
  if (val === null || val === undefined || val === '') return null;
  if (Array.isArray(val)) {
    if (val.length === 0) return null;
    return val.map((v, i) => (
      typeof v === 'object'
        ? <div key={i} style={{ marginBottom: 2 }}>{JSON.stringify(v)}</div>
        : <div key={i}>{String(v)}</div>
    ));
  }
  if (typeof val === 'object') return JSON.stringify(val, null, 2);
  return String(val);
}

function tryParseJson(val) {
  if (!val) return val;
  if (typeof val === 'string') {
    try { return JSON.parse(val); } catch { return val; }
  }
  return val;
}

function FieldRow({ label, value, raw }) {
  const parsed = raw ? value : tryParseJson(value);
  const rendered = renderValue(parsed);
  const isEmpty = rendered === null;

  return (
    <div style={FIELD_ROW}>
      <div style={FIELD_LABEL}>{label}</div>
      {isEmpty
        ? <div style={EMPTY_VALUE}>—</div>
        : <div style={FIELD_VALUE}>{rendered}</div>
      }
    </div>
  );
}

function SectionHeader({ title, color, fieldCount, filledCount }) {
  return (
    <div style={SECTION_HEADER}>
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
        {color && <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />}
        <span style={SECTION_TITLE}>{title}</span>
      </div>
      {fieldCount !== undefined && (
        <span style={STATUS_BADGE(filledCount === fieldCount)}>
          {filledCount === fieldCount
            ? <><CheckCircle size={11} /> {filledCount}/{fieldCount}</>
            : filledCount === 0
              ? <><MinusCircle size={11} /> 0/{fieldCount}</>
              : <><AlertCircle size={11} /> {filledCount}/{fieldCount}</>
          }
        </span>
      )}
    </div>
  );
}

function countFilled(obj, fields) {
  if (!obj) return 0;
  return fields.filter(f => {
    const v = tryParseJson(obj[f]);
    if (v === null || v === undefined || v === '') return false;
    if (Array.isArray(v)) return v.length > 0;
    return true;
  }).length;
}

// ── Component ───────────────────────────────────────────────────────────────

export default function BrandDnaDebugScreen() {
  const { brandId } = useParams();
  const navigate = useNavigate();

  const [brand, setBrand] = useState(null);
  const [assets, setAssets] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [brandRes, assetsRes, promptRes] = await Promise.all([
        brandDnaActions.getBrand(brandId),
        brandDnaActions.listBrandAssets(brandId).catch(() => ({ success: false, data: [] })),
        brandDnaActions.getBrandPrompt(brandId).catch(() => ({ success: false, data: { prompt: '' } })),
      ]);
      if (brandRes.success) setBrand(brandRes.data);
      else { message.error('Failed to load brand'); navigate('/brand-dna'); return; }
      setAssets(assetsRes?.data || []);
      setPrompt(promptRes?.data?.prompt || '');
    } catch {
      message.error('Failed to load brand data');
      navigate('/brand-dna');
    } finally {
      setLoading(false);
    }
  }, [brandId, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCopyPrompt = () => {
    navigator.clipboard.writeText(prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div style={PAGE}>
        <div style={{ color: colors.text.tertiary, fontSize: 14 }}>Loading brand debug data...</div>
      </div>
    );
  }

  if (!brand) return null;

  // Pillar field definitions
  const CORE_FIELDS = ['mission', 'value_propositions', 'keywords_use', 'keywords_avoid', 'product_categories', 'good_copy_examples', 'bad_copy_examples', 'content_themes'];
  const MARKET_FIELDS = ['target_age_range', 'target_gender', 'target_location', 'pain_points', 'buying_triggers', 'customer_mindset', 'differentiator'];
  const VOICE_FIELDS = ['tone_primary', 'tone_descriptors', 'formality_level', 'humor_level', 'energy_level', 'sentence_style', 'perspective', 'personality_if_human', 'sample_phrases', 'communication_dos', 'communication_donts'];
  const VISUAL_FIELDS = ['color_primary', 'color_secondary', 'color_accent', 'aesthetic_category', 'mood', 'photography_style', 'photography_style_detail', 'typography_personality', 'composition_preferences', 'heading_fonts', 'body_fonts', 'visual_donts', 'platform_guidelines'];

  const core = brand.core || {};
  const market = brand.market || {};
  const voice = brand.voice || {};
  const visual = brand.visual || {};
  const products = brand.products || [];
  const competitors = brand.competitors || [];
  const moodboard = brand.moodboard || [];

  const charCount = prompt.length;
  const tokenEstimate = Math.ceil(charCount / 4);

  return (
    <div style={PAGE}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.lg }}>
        <div
          onClick={() => navigate(`/brand-dna/${brandId}`)}
          style={{ cursor: 'pointer', color: colors.text.tertiary, display: 'flex', alignItems: 'center', gap: 4 }}
        >
          <ArrowLeft size={16} />
          Back
        </div>
        <span style={{ color: colors.text.disabled }}>|</span>
        <span style={{ fontSize: 18, fontWeight: 700, color: colors.text.primary }}>{brand.brand_name}</span>
        <span style={{ fontSize: 13, color: colors.text.disabled, fontFamily: 'monospace' }}>debug view</span>
      </div>

      {/* ── Brand Basics ──────────────────────────────────────────── */}
      <div style={SECTION}>
        <SectionHeader title="Brand Basics" />
        <FieldRow label="brand_id" value={brand.brand_id} raw />
        <FieldRow label="brand_name" value={brand.brand_name} raw />
        <FieldRow label="industry" value={brand.industry} />
        <FieldRow label="tagline" value={brand.tagline} />
        <FieldRow label="source_url" value={brand.source_url} />
        <FieldRow label="status" value={brand.status} raw />
        <FieldRow label="completion_score" value={brand.completion_score != null ? `${brand.completion_score}%` : null} raw />
        <FieldRow label="is_default" value={brand.is_default ? 'Yes' : 'No'} raw />
        <FieldRow label="brand_language" value={brand.brand_language} />
        <FieldRow label="use_emojis" value={brand.use_emojis ? 'Yes' : 'No'} raw />
        <FieldRow label="brand_design_description" value={brand.brand_design_description} />
        <FieldRow label="extra_guidelines" value={brand.extra_guidelines} />
        <FieldRow label="social_media_urls" value={brand.social_media_urls} />
        <FieldRow label="logo_url" value={brand.logo_url} />
      </div>

      {/* ── Core Identity ─────────────────────────────────────────── */}
      <div style={SECTION}>
        <SectionHeader
          title="Core Identity"
          color={colors.brand.primary}
          fieldCount={CORE_FIELDS.length}
          filledCount={countFilled(core, CORE_FIELDS)}
        />
        {CORE_FIELDS.map(f => <FieldRow key={f} label={f} value={core[f]} />)}
      </div>

      {/* ── Market ────────────────────────────────────────────────── */}
      <div style={SECTION}>
        <SectionHeader
          title="Your Market"
          color="#f093fb"
          fieldCount={MARKET_FIELDS.length}
          filledCount={countFilled(market, MARKET_FIELDS)}
        />
        {MARKET_FIELDS.map(f => <FieldRow key={f} label={f} value={market[f]} />)}
      </div>

      {/* ── Voice ─────────────────────────────────────────────────── */}
      <div style={SECTION}>
        <SectionHeader
          title="Brand Voice"
          color="#fa8c16"
          fieldCount={VOICE_FIELDS.length}
          filledCount={countFilled(voice, VOICE_FIELDS)}
        />
        {VOICE_FIELDS.map(f => <FieldRow key={f} label={f} value={voice[f]} />)}
      </div>

      {/* ── Visual ────────────────────────────────────────────────── */}
      <div style={SECTION}>
        <SectionHeader
          title="Visual Style"
          color="#52c41a"
          fieldCount={VISUAL_FIELDS.length}
          filledCount={countFilled(visual, VISUAL_FIELDS)}
        />
        {VISUAL_FIELDS.map(f => {
          const val = visual[f];
          // Show color swatches for color fields
          if (f.startsWith('color_') && val) {
            return (
              <div key={f} style={FIELD_ROW}>
                <div style={FIELD_LABEL}>{f}</div>
                <div style={FIELD_VALUE}>
                  <span style={{
                    display: 'inline-block',
                    width: 14, height: 14,
                    borderRadius: 3,
                    background: val,
                    border: '1px solid rgba(255,255,255,0.2)',
                    verticalAlign: 'middle',
                    marginRight: 8,
                  }} />
                  {val}
                </div>
              </div>
            );
          }
          return <FieldRow key={f} label={f} value={val} />;
        })}
      </div>

      {/* ── Products ──────────────────────────────────────────────── */}
      <div style={SECTION}>
        <SectionHeader title={`Products (${products.length})`} />
        {products.length === 0 ? (
          <div style={{ padding: spacing.md, color: colors.text.disabled, fontSize: 13 }}>No products</div>
        ) : (
          products.map((p, i) => (
            <div key={p.brand_media_group_id || i} style={{ ...FIELD_ROW, flexDirection: 'column', padding: spacing.sm, gap: 4 }}>
              <div style={{ fontWeight: 600, color: colors.text.primary }}>{p.name || 'Unnamed'}</div>
              {p.description && <div style={{ color: colors.text.secondary, fontSize: 12 }}>{p.description}</div>}
              {p.metadata && (
                <div style={{ color: colors.text.tertiary, fontSize: 11 }}>
                  metadata: {typeof p.metadata === 'string' ? p.metadata : JSON.stringify(p.metadata)}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* ── Competitors ───────────────────────────────────────────── */}
      <div style={SECTION}>
        <SectionHeader title={`Competitors (${competitors.length})`} />
        {competitors.length === 0 ? (
          <div style={{ padding: spacing.md, color: colors.text.disabled, fontSize: 13 }}>No competitors</div>
        ) : (
          competitors.map((c, i) => (
            <div key={c.brand_competitor_id || i} style={FIELD_ROW}>
              <div style={FIELD_LABEL}>{c.competitor_name}</div>
              <div style={FIELD_VALUE}>
                {c.competitor_url && <div style={{ fontSize: 12, color: colors.text.tertiary }}>{c.competitor_url}</div>}
                {c.competitor_positioning && <div>{c.competitor_positioning}</div>}
                {!c.competitor_url && !c.competitor_positioning && <span style={{ color: colors.text.disabled }}>—</span>}
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Moodboard ─────────────────────────────────────────────── */}
      <div style={SECTION}>
        <SectionHeader title={`Moodboard (${moodboard.length})`} />
        {moodboard.length === 0 ? (
          <div style={{ padding: spacing.md, color: colors.text.disabled, fontSize: 13 }}>No moodboard items</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.xs, padding: spacing.sm }}>
            {moodboard.map((m, i) => (
              <div key={m.brand_moodboard_id || i} style={{ textAlign: 'center' }}>
                {m.image_url ? (
                  <img src={m.image_url} alt={m.description || ''} style={IMAGE_THUMB} />
                ) : (
                  <div style={{ ...IMAGE_THUMB, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.text.disabled, fontSize: 10 }}>no img</div>
                )}
                {m.description && (
                  <div style={{ fontSize: 10, color: colors.text.tertiary, maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Brand Assets ──────────────────────────────────────────── */}
      <div style={SECTION}>
        <SectionHeader title={`Brand Assets (${assets.length})`} />
        {assets.length === 0 ? (
          <div style={{ padding: spacing.md, color: colors.text.disabled, fontSize: 13 }}>No brand assets</div>
        ) : (
          assets.map((a, i) => (
            <div key={a.brand_asset_id || i} style={FIELD_ROW}>
              <div style={FIELD_LABEL}>
                <span style={{
                  padding: '1px 6px', borderRadius: 4,
                  background: 'rgba(102,126,234,0.15)', color: '#667eea',
                  fontSize: 11, fontWeight: 500,
                }}>
                  {a.asset_type || 'other'}
                </span>
              </div>
              <div style={{ ...FIELD_VALUE, display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                {a.external_url && <img src={a.external_url} alt="" style={{ ...IMAGE_THUMB, width: 40, height: 40 }} />}
                <div>
                  {a.file_name && <div style={{ fontSize: 12 }}>{a.file_name}</div>}
                  {a.external_url && (
                    <div style={{ fontSize: 11, color: colors.text.tertiary, wordBreak: 'break-all' }}>{a.external_url}</div>
                  )}
                  {a.ai_caption && (
                    <div style={{ fontSize: 11, color: colors.text.secondary, marginTop: 2 }}>Caption: {a.ai_caption}</div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── AI Prompt (What the Agent Sees) ────────────────────────── */}
      <div style={SECTION}>
        <div style={SECTION_HEADER}>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#667eea' }} />
            <span style={SECTION_TITLE}>AI Prompt (What Agent Sees)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
            <span style={{
              fontSize: 11,
              color: charCount > 4000 ? colors.status.error : charCount > 3500 ? colors.status.warning : colors.text.tertiary,
              fontFamily: 'monospace',
            }}>
              {charCount} chars / ~{tokenEstimate} tokens
              {charCount > 4000 && ' (TRUNCATED)'}
              {charCount > 3500 && charCount <= 4000 && ' (near limit)'}
            </span>
            {prompt && (
              <button
                onClick={handleCopyPrompt}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '3px 8px', borderRadius: 4,
                  background: colors.surface.primary,
                  border: `1px solid ${colors.border.primary}`,
                  color: copied ? colors.status.success : colors.text.tertiary,
                  fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                {copied ? <Check size={11} /> : <Copy size={11} />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            )}
          </div>
        </div>
        <div style={{
          padding: spacing.md,
          fontSize: 12,
          lineHeight: 1.6,
          color: colors.text.secondary,
          whiteSpace: 'pre-wrap',
          fontFamily: 'monospace',
          maxHeight: 500,
          overflow: 'auto',
          background: 'rgba(0,0,0,0.3)',
        }}>
          {prompt || <span style={{ color: colors.text.disabled }}>No prompt generated — fill in brand pillars first</span>}
        </div>
      </div>

      {/* ── Summary Stats ─────────────────────────────────────────── */}
      <div style={{
        padding: spacing.md,
        background: colors.background.secondary,
        border: `1px solid ${colors.border.secondary}`,
        borderRadius: radius.md,
        marginBottom: spacing.xxxl,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: spacing.sm,
      }}>
        {[
          { label: 'Core Identity', filled: countFilled(core, CORE_FIELDS), total: CORE_FIELDS.length },
          { label: 'Market', filled: countFilled(market, MARKET_FIELDS), total: MARKET_FIELDS.length },
          { label: 'Voice', filled: countFilled(voice, VOICE_FIELDS), total: VOICE_FIELDS.length },
          { label: 'Visual', filled: countFilled(visual, VISUAL_FIELDS), total: VISUAL_FIELDS.length },
          { label: 'Products', filled: products.length, total: null },
          { label: 'Competitors', filled: competitors.length, total: null },
          { label: 'Moodboard', filled: moodboard.length, total: null },
          { label: 'Assets', filled: assets.length, total: null },
          { label: 'AI Prompt', filled: charCount, total: '4000 max' },
        ].map(s => (
          <div key={s.label} style={{ textAlign: 'center', padding: spacing.xs }}>
            <div style={{
              fontSize: 20, fontWeight: 700,
              color: s.total === null
                ? (s.filled > 0 ? colors.status.success : colors.text.disabled)
                : (s.filled === s.total ? colors.status.success : s.filled > 0 ? colors.status.warning : colors.text.disabled),
            }}>
              {s.total !== null && s.total !== '4000 max' ? `${s.filled}/${s.total}` : s.filled}
            </div>
            <div style={{ fontSize: 11, color: colors.text.tertiary }}>{s.label}</div>
            {s.total === '4000 max' && (
              <div style={{ fontSize: 10, color: colors.text.disabled }}>/ 4000 max</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
